/**
 * Onboard Client Modal
 *
 * The reusable onboarding flow surfaced as a single dashboard action. Picks a
 * plan + a few fields and calls the `onboardClient` callable, which idempotently
 * creates the client, Stripe customer, self-service portal link, the plan's
 * subscriptions, an optional GCP usage link, and an optional one-time invoice.
 *
 * On success it shows the portal URL to send the client — they save their own
 * card there and recurring auto-charges begin. No per-client manual setup.
 */

import React, { useState, useMemo } from 'react';
import { onboardClient } from '../services/clientService';
import { ONBOARDING_PLANS, PLAN_OPTIONS } from '../config/onboardingPlans';
import './Modal.css';

const round2 = (n) => Math.round(n * 100) / 100;
const usd = (cur, n) => `${cur} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OnboardClientModal = ({ onClose, onComplete }) => {
  const [planId, setPlanId] = useState('managed-basic');
  const plan = ONBOARDING_PLANS[planId];

  const [form, setForm] = useState({
    company: '',
    email: '',
    name: '',
    phone: '',
    currency: '',
    seats: 1,
    gcpProjectId: '',
    billingAccountId: '',
    buildFee: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const currency = form.currency || plan.currency;
  const seats = Number(form.seats) || 1;

  // Live preview of what the server will create.
  const preview = useMemo(() => {
    const lines = (plan.subscriptions || []).map((s) => {
      const isSeat = s.key === 'workspace';
      const rate = isSeat ? s.perSeatRate : s.rate;
      const amount = round2(rate * (isSeat ? seats : 1));
      return {
        label: s.description,
        amount: usd(currency, amount),
        period: s.frequency === 'yearly' ? '/yr' : '/mo',
      };
    });
    let oneTime = null;
    if (plan.oneTime) {
      const amt = form.buildFee !== '' && form.buildFee != null ? Number(form.buildFee) : plan.oneTime.defaultAmount;
      oneTime = {
        label: plan.oneTime.description,
        amount: `${plan.oneTime.currency} ${Number(amt).toLocaleString()}`,
      };
    }
    return { lines, oneTime };
  }, [plan, seats, currency, form.buildFee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.company.trim() || !form.email.trim()) {
      setError('Company and email are required.');
      return;
    }
    setLoading(true);
    try {
      const params = {
        workspaceSeats: seats,
        gcpProjectId: form.gcpProjectId.trim() || undefined,
        billingAccountId: form.billingAccountId.trim() || undefined,
        buildFee: form.buildFee !== '' ? Number(form.buildFee) : undefined,
      };
      const res = await onboardClient({
        client: {
          company: form.company.trim(),
          email: form.email.trim(),
          name: form.name.trim() || undefined,
          phone: form.phone.trim() || undefined,
          currency: form.currency || undefined,
        },
        planId,
        params,
      });
      setResult(res);
      if (onComplete) onComplete(res);
    } catch (err) {
      setError(err.message || 'Onboarding failed.');
    } finally {
      setLoading(false);
    }
  };

  const copyPortal = async () => {
    try {
      await navigator.clipboard.writeText(result.portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the URL is visible to copy manually */
    }
  };

  // ── Success state ────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>✅ Client onboarded</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">
            <p style={{ marginTop: 0 }}>
              <strong>{form.company}</strong> is set up. Send them this self-service link to
              save their card — recurring auto-charges begin afterwards.
            </p>

            <div className="form-group">
              <label>Self-service portal link</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input className="input" readOnly value={result.portalUrl} style={{ flex: 1 }} />
                <button type="button" className="btn btn-primary" onClick={copyPortal} style={{ whiteSpace: 'nowrap' }}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <ul style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7 }}>
              <li>Client record: <code>{result.clientId}</code></li>
              <li>Stripe customer: <code>{result.customerId}</code></li>
              <li>Subscriptions created/linked: {result.subscriptionIds?.length || 0}</li>
              {result.invoiceNumber && <li>One-time invoice: <code>{result.invoiceNumber}</code></li>}
            </ul>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form state ─────────────────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Onboard Client</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="plan">Plan *</label>
              <select id="plan" className="input" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                {PLAN_OPTIONS.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company">Company *</label>
                <input id="company" className="input" value={form.company} onChange={(e) => set('company', e.target.value)} required />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input id="email" type="email" className="input" value={form.email} onChange={(e) => set('email', e.target.value)} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Contact Name</label>
                <input id="name" className="input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Defaults to company name" />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input id="phone" type="tel" className="input" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="currency">Currency</label>
                <select id="currency" className="input" value={form.currency} onChange={(e) => set('currency', e.target.value)}>
                  <option value="">Plan default ({plan.currency})</option>
                  <option value="USD">USD</option>
                  <option value="PHP">PHP</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="seats">Workspace Seats</label>
                <input id="seats" type="number" min="1" className="input" value={form.seats} onChange={(e) => set('seats', e.target.value)} />
              </div>
            </div>

            {plan.oneTime && (
              <div className="form-group">
                <label htmlFor="buildFee">Build Fee ({plan.oneTime.currency}, one-time)</label>
                <input
                  id="buildFee"
                  type="number"
                  min="0"
                  step="0.01"
                  className="input"
                  value={form.buildFee}
                  onChange={(e) => set('buildFee', e.target.value)}
                  placeholder={`Default ${plan.oneTime.defaultAmount.toLocaleString()}`}
                />
                <span className="help-text">Leave blank to use the plan default ({plan.oneTime.currency} {plan.oneTime.defaultAmount.toLocaleString()}).</span>
              </div>
            )}

            <div className="form-section">
              <div className="section-content">
                <p className="section-description">GCP / Firebase usage (optional) — billed at cost +{(plan.gcpMarkup * 100)}%.</p>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="gcpProjectId">GCP Project ID</label>
                    <input id="gcpProjectId" className="input" value={form.gcpProjectId} onChange={(e) => set('gcpProjectId', e.target.value)} placeholder="e.g. acme-prod" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="billingAccountId">Billing Account ID</label>
                    <input id="billingAccountId" className="input" value={form.billingAccountId} onChange={(e) => set('billingAccountId', e.target.value)} placeholder="optional" />
                  </div>
                </div>
              </div>
            </div>

            {/* Live preview */}
            <div className="pricing-preview">
              <strong>Will create:</strong>
              <ul>
                {preview.lines.map((l, i) => (
                  <li key={i}>{l.label}: {l.amount}{l.period}{l.period === '/mo' && plan.subscriptions[i]?.key === 'workspace' ? ` (${seats} seat${seats === 1 ? '' : 's'})` : ''}</li>
                ))}
                {preview.oneTime && <li>One-time invoice: {preview.oneTime.amount} — {preview.oneTime.label}</li>}
                {form.gcpProjectId.trim() && <li>GCP usage link: {form.gcpProjectId.trim()} (+{plan.gcpMarkup * 100}%)</li>}
                <li>Self-service portal link (client saves their own card)</li>
              </ul>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Onboarding…' : 'Onboard Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnboardClientModal;
